/**
 * WO-D2.M — first-run onboarding hub. Shows license status + the intake address and routes the
 * owner to the centerpiece (Import history) and account setup. No @smartbox/* (invariant 1).
 *
 * WO-M0 — rebuilt on the design system: Screen / Card / ListRow / Button / Text / Badge.
 */
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { getLicense, getInboundAddress } from '../../src/api/account';
import { Screen, Text, Card, Button, ListRow, Badge, licenseBadge } from '../../src/components/ui';
import { theme } from '../../src/theme';

export default function OnboardingScreen() {
  const router = useRouter();
  const license = useQuery({ queryKey: ['license'], queryFn: getLicense });
  const inbound = useQuery({ queryKey: ['inbound-address'], queryFn: getInboundAddress });

  const licBadge = license.data ? licenseBadge(license.data.status ?? 'trial') : null;

  return (
    <Screen scroll>
      <Text variant="title">Let's get you set up</Text>

      {/* Plan card */}
      <Card>
        <Text variant="label" color="secondary">Your plan</Text>
        {license.isLoading ? (
          <ActivityIndicator accessibilityLabel="Loading plan" />
        ) : (
          <View style={styles.planRow}>
            <Text variant="bodyStrong">
              {license.data?.has_license
                ? `${license.data.plan ?? 'Subscription'}`
                : 'Trial'}
              {license.data?.seats ? ` · ${license.data.seats} seats` : ''}
            </Text>
            {licBadge ? <Badge status={licBadge.key} label={licBadge.label} /> : null}
          </View>
        )}
      </Card>

      {/* Pick your trades — shapes the prepopulated service suggestions (WO-D5) */}
      <Card>
        <Text variant="label" color="secondary">First, your trades</Text>
        <Text variant="subhead">Pick the trades you work in</Text>
        <Text variant="body" color="secondary">
          We'll prepopulate your estimates with the services you do most. Choose as many as you like.
        </Text>
        <Button
          title="Pick your trades"
          variant="primary"
          onPress={() => router.push('/(app)/account/trades')}
          accessibilityLabel="Pick your trades"
        />
      </Card>

      {/* Import CTA — the centerpiece */}
      <Card>
        <Text variant="label" color="secondary">Get started</Text>
        <Text variant="subhead">Import your history</Text>
        <Text variant="body" color="secondary">
          Drop in receipts, invoices and your Home Depot / Lowe's / eBay order exports. We'll read
          them and use them to sharpen your estimates. One spot — we handle the rest.
        </Text>
        <Button
          title="Import your history"
          variant="primary"
          size="lg"
          onPress={() => router.push('/(app)/import')}
          accessibilityLabel="Import your history"
        />
      </Card>

      {/* Inbound address */}
      <Card>
        <Text variant="label" color="secondary">Your intake address</Text>
        <Text variant="body" color="secondary">
          Forward receipts and quote requests to the address below and they'll appear in your review
          queue.
        </Text>
        {inbound.isLoading ? (
          <ActivityIndicator accessibilityLabel="Loading intake address" />
        ) : (
          <Text variant="data" selectable accessibilityLabel="Intake email address">
            {inbound.data?.address ?? '…'}
          </Text>
        )}
      </Card>

      {/* Account setup links */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">Set up your account</Text>
      </View>
      <View style={styles.list}>
        <ListRow
          title="Invite your team"
          subtitle="Users & roles"
          onPress={() => router.push('/(app)/account/users')}
        />
        <ListRow
          title="Configure senders"
          subtitle="Text & email"
          onPress={() => router.push('/(app)/account/comms')}
        />
        <ListRow
          title="Billing & plan"
          subtitle="Manage your subscription"
          onPress={() => router.push('/(app)/account/billing')}
        />
      </View>

      <Button
        title="Go to dashboard"
        variant="ghost"
        size="lg"
        onPress={() => router.replace('/home')}
        accessibilityLabel="Go to dashboard"
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  planRow: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
  section: { marginTop: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
});
